/**
 * Operating Costs page — admin + owner only.
 *
 * Two sub-views:
 *   Ledger  — full list of cost entries with filters, add, edit
 *   P&L     — paper profit vs real profit comparison for a chosen period
 */

import { useState, type FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Icon } from '../components/Icon'
import { useAuth, authHeaders } from '../lib/auth'
import { useTranslation } from 'react-i18next'

// ── types ─────────────────────────────────────────────────────────────────────

interface CostEntry {
  cost_id:            number
  cost_date:          string
  category:           string
  description:        string
  amount_usd_minor:   number
  payment_method:     string | null
  person_name:        string | null
  recurring:          boolean
  recurrence_note:    string | null
  notes:              string | null
  created_at:         string
}

interface CostSummary {
  total_usd_minor: number
  by_category: { category: string; total_usd_minor: number; count: number }[]
}

interface RealProfitRow {
  revenue_minor:      number
  paper_profit_minor: number
  total_costs_minor:  number
  real_profit_minor:  number
  paper_margin_pct:   number
  real_margin_pct:    number
  shipment_count:     number
}

// ── helpers ───────────────────────────────────────────────────────────────────

const CATEGORIES = ['personnel', 'banking', 'stripe', 'infrastructure', 'software', 'office', 'other'] as const
type Category = typeof CATEGORIES[number]

const CAT_BADGE: Record<Category, string> = {
  personnel:      'bg-royal-100 text-royal-700',
  banking:        'bg-amber-100 text-amber-700',
  stripe:         'bg-plum-100 text-plum-700',
  infrastructure: 'bg-ink-100 text-ink-600',
  software:       'bg-mint-100 text-mint-700',
  office:         'bg-honey-100 text-honey-700',
  other:          'bg-ink-50 text-ink-500',
}

const PAYMENT_METHODS = ['chase_ach', 'chase_wire', 'chase_zelle', 'stripe', 'card', 'other']

function fmtMoney(minor: number): string {
  return (minor / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

function pctColor(pct: number): string {
  if (pct >= 30) return 'text-mint-700'
  if (pct >= 15) return 'text-amber-700'
  if (pct >= 0)  return 'text-flame-600'
  return 'text-flame-700'
}

// ── Add / Edit Cost Modal ────────────────────────────────────────────────────

interface CostModalProps {
  entry?: CostEntry | null
  onClose: () => void
}

function CostModal({ entry, onClose }: CostModalProps) {
  const { t }      = useTranslation()
  const { token }  = useAuth()
  const qc         = useQueryClient()
  const isEdit     = !!entry

  const today = new Date().toISOString().split('T')[0]

  const [form, setForm] = useState({
    cost_date:       entry ? entry.cost_date.split('T')[0] : today,
    category:        entry?.category ?? 'other',
    description:     entry?.description ?? '',
    amount_dollars:  entry ? String(entry.amount_usd_minor / 100) : '',
    payment_method:  entry?.payment_method ?? '',
    recurring:       entry?.recurring ?? false,
    recurrence_note: entry?.recurrence_note ?? '',
    notes:           entry?.notes ?? '',
  })
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: async () => {
      const amount_usd_minor = Math.round(parseFloat(form.amount_dollars) * 100)
      if (isNaN(amount_usd_minor) || amount_usd_minor <= 0) throw new Error('Enter a valid amount')

      const body = {
        cost_date:       form.cost_date,
        category:        form.category,
        description:     form.description.trim(),
        amount_usd_minor,
        payment_method:  form.payment_method || null,
        recurring:       form.recurring,
        recurrence_note: form.recurrence_note || null,
        notes:           form.notes || null,
      }

      const url    = isEdit ? `/api/costs/${entry!.cost_id}` : '/api/costs'
      const method = isEdit ? 'PATCH' : 'POST'

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
        body: JSON.stringify(body),
      })
      if (!res.ok) { const d = await res.json(); throw new Error(d.error) }
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['costs'] })
      qc.invalidateQueries({ queryKey: ['costs-summary'] })
      qc.invalidateQueries({ queryKey: ['real-profit'] })
      onClose()
    },
    onError: (e: Error) => setError(e.message),
  })

  const set = (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [k]: e.target.value }))

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200">
          <h2 className="text-[15px] font-semibold">{isEdit ? 'Edit cost' : 'Add cost'}</h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><Icon name="x" /></button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">Date *</label>
              <input className="form-input" type="date" value={form.cost_date} onChange={set('cost_date')} />
            </div>
            <div>
              <label className="form-label">Category *</label>
              <select className="form-select" value={form.category} onChange={set('category')}>
                {CATEGORIES.map(c => (
                  <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="form-label">Description *</label>
            <input className="form-input" value={form.description} onChange={set('description')} placeholder="e.g. Monthly AWS bill, Sahin salary – June" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">Amount (USD) *</label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-ink-400 text-sm">$</span>
                <input
                  className="form-input pl-6"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={form.amount_dollars}
                  onChange={set('amount_dollars')}
                />
              </div>
            </div>
            <div>
              <label className="form-label">Payment method</label>
              <select className="form-select" value={form.payment_method} onChange={set('payment_method')}>
                <option value="">—</option>
                {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m.replace('_', ' ')}</option>)}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              id="recurring"
              type="checkbox"
              checked={form.recurring}
              onChange={e => setForm(f => ({ ...f, recurring: e.target.checked }))}
              className="rounded border-ink-300"
            />
            <label htmlFor="recurring" className="text-[13px] text-ink-700 cursor-pointer">Recurring</label>
          </div>

          {form.recurring && (
            <div>
              <label className="form-label">Recurrence note</label>
              <input className="form-input" value={form.recurrence_note} onChange={set('recurrence_note')} placeholder="e.g. Monthly AWS bill — auto-charged 1st of month" />
            </div>
          )}

          <div>
            <label className="form-label">Notes</label>
            <textarea className="form-input min-h-[56px] resize-none" value={form.notes} onChange={set('notes')} />
          </div>

          {error && (
            <div className="text-sm text-flame-700 bg-flame-50 border border-flame-200 rounded-xl px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-ink-200">
          <button onClick={onClose} className="btn-ghost">{t('common.cancel')}</button>
          <button
            onClick={() => save.mutate()}
            disabled={!form.description.trim() || !form.amount_dollars || !form.cost_date || save.isPending}
            className="btn-primary"
          >
            {save.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add cost'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Delete confirmation ───────────────────────────────────────────────────────

function DeleteCostButton({ costId }: { costId: number }) {
  const { token } = useAuth()
  const qc        = useQueryClient()
  const [step, setStep] = useState<'idle' | 'confirm'>('idle')

  const del = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/costs/${costId}`, {
        method:  'DELETE',
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
        body:    JSON.stringify({ confirmed: true }),
      })
      if (!res.ok) { const d = await res.json(); throw new Error(d.error) }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['costs'] })
      qc.invalidateQueries({ queryKey: ['costs-summary'] })
      qc.invalidateQueries({ queryKey: ['real-profit'] })
    },
  })

  if (step === 'idle') {
    return (
      <button
        onClick={() => setStep('confirm')}
        className="text-ink-400 hover:text-flame-600 transition-colors"
        title="Delete"
      >
        <Icon name="x" className="w-4 h-4" />
      </button>
    )
  }

  return (
    <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
      <span className="text-[11px] text-flame-600">Delete?</span>
      <button
        onClick={() => { del.mutate(); setStep('idle') }}
        className="text-[11px] font-semibold text-white bg-flame-600 px-2 py-0.5 rounded"
        disabled={del.isPending}
      >
        Yes
      </button>
      <button onClick={() => setStep('idle')} className="text-[11px] text-ink-500">
        No
      </button>
    </div>
  )
}

// ── Ledger tab ────────────────────────────────────────────────────────────────

function LedgerTab() {
  const { t } = useTranslation()
  const { token, isOwner } = useAuth()

  const [fromDate, setFromDate] = useState('')
  const [toDate,   setToDate]   = useState('')
  const [category, setCategory] = useState('')
  const [editEntry, setEditEntry] = useState<CostEntry | null>(null)
  const [showAdd,   setShowAdd]  = useState(false)

  const params = new URLSearchParams()
  if (fromDate) params.set('from_date', fromDate)
  if (toDate)   params.set('to_date',   toDate)
  if (category) params.set('category',  category)

  const { data: entries = [], isLoading } = useQuery<CostEntry[]>({
    queryKey: ['costs', fromDate, toDate, category],
    queryFn: () =>
      fetch(`/api/costs?${params}`, { headers: authHeaders(token) }).then(r => r.json()),
  })

  const total = entries.reduce((s, e) => s + e.amount_usd_minor, 0)

  return (
    <div className="space-y-4">
      {/* Filters + add */}
      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="date"
          className="form-input w-36"
          value={fromDate}
          onChange={e => setFromDate(e.target.value)}
        />
        <input
          type="date"
          className="form-input w-36"
          value={toDate}
          onChange={e => setToDate(e.target.value)}
        />
        <select className="form-select w-40" value={category} onChange={e => setCategory(e.target.value)}>
<option value="">{t('costs.all_categories')}</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <div className="ml-auto flex items-center gap-3">
          {entries.length > 0 && (
            <span className="text-[13px] text-ink-500">
              Total: <span className="font-semibold text-ink-900">{fmtMoney(total)}</span>
            </span>
          )}
          <button onClick={() => setShowAdd(true)} className="btn-primary flex items-center gap-2">
            <Icon name="plus" className="w-4 h-4" /> Add cost
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-ink-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-ink-400">
            <Icon name="loader-circle" className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : entries.length === 0 ? (
          <div className="py-16 text-center text-ink-400">
            <Icon name="trending-down" className="w-8 h-8 mx-auto mb-2 opacity-30" />
<p>{t('costs.no_cost_entries')}</p>
          </div>
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-ink-100 text-left text-[11px] font-semibold text-ink-500 uppercase tracking-wide">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Method</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 w-16" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {entries.map(e => (
                <tr key={e.cost_id} className="hover:bg-ink-50 transition-colors">
                  <td className="px-4 py-3 mono text-ink-500">{e.cost_date.split('T')[0]}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${CAT_BADGE[e.category as Category] ?? 'bg-ink-50 text-ink-500'}`}>
                      {e.category}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-ink-900">{e.description}</div>
                    {e.person_name && <div className="text-[11px] text-ink-400">{e.person_name}</div>}
                    {e.recurring && <div className="text-[11px] text-royal-500">↻ recurring</div>}
                    {e.notes && <div className="text-[11px] text-ink-400 italic">{e.notes}</div>}
                  </td>
                  <td className="px-4 py-3 text-ink-500">
                    {e.payment_method?.replace(/_/g, ' ') ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-ink-900">
                    {fmtMoney(e.amount_usd_minor)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 justify-end">
                      <button
                        onClick={() => setEditEntry(e)}
                        className="text-ink-400 hover:text-ink-700 transition-colors"
                        title="Edit"
                      >
                        <Icon name="key" className="w-3.5 h-3.5" />
                      </button>
                      {isOwner && <DeleteCostButton costId={e.cost_id} />}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAdd   && <CostModal onClose={() => setShowAdd(false)} />}
      {editEntry && <CostModal entry={editEntry} onClose={() => setEditEntry(null)} />}
    </div>
  )
}

// ── Real Profit tab ───────────────────────────────────────────────────────────

function RealProfitTab() {
  const { token } = useAuth()
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
  })
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0])

  const params = new URLSearchParams()
  if (fromDate) params.set('from_date', fromDate)
  if (toDate)   params.set('to_date',   toDate)

  const { data: profit, isLoading: loadingP } = useQuery<RealProfitRow>({
    queryKey: ['real-profit', fromDate, toDate],
    queryFn: () =>
      fetch(`/api/costs/real-profit?${params}`, { headers: authHeaders(token) }).then(r => r.json()),
  })

  const { data: summary, isLoading: loadingS } = useQuery<CostSummary>({
    queryKey: ['costs-summary', fromDate, toDate],
    queryFn: () =>
      fetch(`/api/costs/summary?${params}`, { headers: authHeaders(token) }).then(r => r.json()),
  })

  return (
    <div className="space-y-6">
      {/* Date range */}
      <div className="flex items-center gap-3">
        <input type="date" className="form-input w-36" value={fromDate} onChange={e => setFromDate(e.target.value)} />
        <span className="text-ink-400">to</span>
        <input type="date" className="form-input w-36" value={toDate}   onChange={e => setToDate(e.target.value)} />
      </div>

      {/* Paper vs Real profit KPIs */}
      {(loadingP || loadingS) ? (
        <div className="flex items-center gap-2 py-8 text-ink-400">
          <Icon name="loader-circle" className="w-5 h-5 animate-spin" /> Calculating…
        </div>
      ) : profit ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl border border-ink-200 p-5">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-400 mb-1">Revenue</div>
              <div className="text-xl font-bold text-ink-900">{fmtMoney(profit.revenue_minor)}</div>
              <div className="text-[11px] text-ink-400 mt-0.5">{profit.shipment_count.toLocaleString()} shipments</div>
            </div>

            <div className="bg-white rounded-2xl border border-ink-200 p-5">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-400 mb-1">Paper profit</div>
              <div className="text-xl font-bold text-ink-900">{fmtMoney(profit.paper_profit_minor)}</div>
              <div className={`text-[11px] font-medium mt-0.5 ${pctColor(profit.paper_margin_pct)}`}>
                {profit.paper_margin_pct.toFixed(1)}% margin
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-red-200 p-5">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-400 mb-1">Operating costs</div>
              <div className="text-xl font-bold text-flame-700">{fmtMoney(profit.total_costs_minor)}</div>
              <div className="text-[11px] text-ink-400 mt-0.5">
                {profit.revenue_minor > 0
                  ? `${(profit.total_costs_minor / profit.revenue_minor * 100).toFixed(1)}% of revenue`
                  : '—'}
              </div>
            </div>

            <div className={`rounded-2xl border p-5 ${profit.real_profit_minor >= 0 ? 'bg-mint-50 border-mint-200' : 'bg-flame-50 border-flame-200'}`}>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-400 mb-1">Real profit</div>
              <div className={`text-xl font-bold ${profit.real_profit_minor >= 0 ? 'text-mint-700' : 'text-flame-700'}`}>
                {fmtMoney(profit.real_profit_minor)}
              </div>
              <div className={`text-[11px] font-medium mt-0.5 ${pctColor(profit.real_margin_pct)}`}>
                {profit.real_margin_pct.toFixed(1)}% margin
              </div>
            </div>
          </div>

          {/* Cost breakdown */}
          {summary && summary.by_category.length > 0 && (
            <div className="bg-white rounded-2xl border border-ink-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-ink-100 text-[13px] font-semibold text-ink-700">
                Cost breakdown
              </div>
              <table className="w-full text-[13px]">
                <tbody className="divide-y divide-ink-50">
                  {summary.by_category.map(row => (
                    <tr key={row.category}>
                      <td className="px-6 py-3">
                        <span className={`inline-block text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${CAT_BADGE[row.category as Category] ?? 'bg-ink-50 text-ink-500'}`}>
                          {row.category}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-ink-500">{row.count} entries</td>
                      <td className="px-6 py-3 text-right font-medium text-ink-900">
                        {fmtMoney(row.total_usd_minor)}
                      </td>
                      <td className="px-6 py-3 text-right text-ink-400 text-[11px]">
                        {profit.total_costs_minor > 0
                          ? `${(row.total_usd_minor / profit.total_costs_minor * 100).toFixed(1)}%`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t border-ink-200 bg-ink-50 font-semibold">
                    <td className="px-6 py-3 text-ink-700">Total</td>
                    <td />
                    <td className="px-6 py-3 text-right text-ink-900">{fmtMoney(summary.total_usd_minor)}</td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </div>
  )
}

// ── Main view ─────────────────────────────────────────────────────────────────

type Tab = 'ledger' | 'real-profit'

export function CostsView() {
  const { t } = useTranslation()
  const [tab, setTab] = useState<Tab>('ledger')

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Operating Costs</h1>
          <p className="text-sm text-ink-500 mt-0.5">Track costs, compare paper vs real profit</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-ink-100 rounded-xl p-1 w-fit">
        {([['ledger', 'Ledger'], ['real-profit', 'Paper vs Real']] as [Tab, string][]).map(([t, label]) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              tab === t ? 'bg-white shadow-sm text-ink-900' : 'text-ink-500 hover:text-ink-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'ledger'      && <LedgerTab />}
      {tab === 'real-profit' && <RealProfitTab />}
    </div>
  )
}
